/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal, ModalProps, Steps, Button, message } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { TemplateSelectionStep } from './steps/TemplateSelectionStep';
import { FolderBuilderStep } from './steps/FolderBuilderStep';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

export interface ProjectBuilderModalProps extends Partial<ModalProps> {
  onCancel?: () => void;
}

export const ProjectBuilderModal: React.FC<ProjectBuilderModalProps> = ({
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [customFolders, setCustomFolders] = useState<any[]>([]);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setCurrentStep(1);
  };

  const handleSkipTemplate = () => {
    setSelectedTemplateId(null);
    setCurrentStep(1);
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    // This will call either initializeArchiveFolderStructure or syncArchiveFolderStructure
    // depending on whether folders already exist in the project
    message.warning(intl.formatMessage({ id: 'save-not-implemented' }));
    // Future implementation:
    // if (projectFoldersQuery.data && projectFoldersQuery.data.length > 0) {
    //   // Use syncArchiveFolderStructure for updates
    // } else {
    //   // Use initializeArchiveFolderStructure for initial setup
    // }
  };

  const handleClose = () => {
    onCancel?.();
  };

  const steps = [
    {
      title: intl.formatMessage({ id: 'select-template' }),
      content: (
        <TemplateSelectionStep
          onTemplateSelect={handleTemplateSelect}
          onSkip={handleSkipTemplate}
        />
      ),
    },
    {
      title: intl.formatMessage({ id: 'build-structure' }),
      content: (
        <FolderBuilderStep
          templateId={selectedTemplateId}
          customFolders={customFolders}
          setCustomFolders={setCustomFolders}
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'project-builder' })}
          icon={['fad', 'sliders-h-square']}
        />
      }
      width={1200}
      centered
      onCancel={onCancel}
      maskClosable={false}
      closable={true}
      footer={
        <div css={styles.footer}>
          {currentStep > 0 && (
            <Button onClick={handleBack}>
              {intl.formatMessage({ id: 'back' })}
            </Button>
          )}
          {currentStep === 1 && (
            <Button type="primary" onClick={handleSave}>
              {intl.formatMessage({ id: 'save' })}
            </Button>
          )}
        </div>
      }
      {...modalProps}>
      <Steps current={currentStep} css={styles.steps}>
        {steps.map((step) => (
          <Steps.Step key={step.title} title={step.title} />
        ))}
      </Steps>
      <div css={styles.content}>{steps[currentStep].content}</div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  steps: {
    marginBottom: 32,
    '.ant-steps-item-process .ant-steps-item-icon': {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    '.ant-steps-item-finish .ant-steps-item-icon': {
      borderColor: theme.colors.primary,
      '& .ant-steps-icon': {
        color: theme.colors.primary,
      },
    },
  },
  content: {
    minHeight: 350,
    marginTop: 24,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0 0 0',
    borderTop: `1px solid ${theme.colors.border}`,
    marginTop: 16,
  },
});
